import React, { useEffect, useState } from "react";
import { get } from "lodash";
import PropTypes from "prop-types";
import { TextField, Box, Grid, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import SearchInput from "../../components/SearchInput/SearchInput";
import { formatDate } from "../../utils/datetime";
import useScopedTranslation from "../../hooks/useScopedTranslation";
import { useUsers } from "../../hooks/users";
import { Tooltip } from "../../components/Tooltip";

const useStyles = makeStyles(theme => ({
  textField: {
    backgroundColor: theme.palette.common.white
  },
  adjustmentGridItem: {
    maxWidth: "unset",
    minWidth: 200,
    flex: "1 1 50%",
    [theme.breakpoints.down("sm")]: {
      flex: "0 1 100%"
    }
  }
}));

const UserSelect = ({ value, onChange, disabled, helperText, error }) => {
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const t = useScopedTranslation("validate.order.details.adjustedEmployee");
  const { getUsers } = useUsers();

  const filterOptions = (options, { inputValue }) =>
    options.filter(
      ({ fullName }) =>
        fullName.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
    );

  useEffect(() => {
    setIsLoading(true);
    getUsers({
      fields: {
        user: ["fullName", "location"],
        location: ["name"]
      },
      include: ["location"]
    }).then(({ data }) => {
      setUsers(data);
      setIsLoading(false);
    });
  }, []);

  const renderOption = (props, option) => {
    return (
      <li {...props}>
        <Box display="flex" flexDirection="column" py={1}>
          <Typography variant="subtitle2">{option.fullName}</Typography>
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            {option.location.name}
          </Typography>
        </Box>
      </li>
    );
  };

  return (
    <SearchInput
      items={users}
      renderOption={renderOption}
      getOptionLabel={({ fullName }) => fullName}
      filterOptions={filterOptions}
      label={t("label")}
      placeholder={t("placeholder")}
      itemSize={60}
      onChange={(e, value) => onChange(value)}
      loading={isLoading}
      helperText={error ? t("errorMsg") : helperText}
      InputProps={{ className: classes.textField }}
      value={value}
      getOptionSelected={(option, value) => option.id === value.id}
      disabled={disabled}
      error={error}
    />
  );
};

const characterLimit = 600;
const maxScore = 100;

const AdjustmentField = ({ disabled, children }) => {
  const t = useScopedTranslation("validate.order.details");
  return !disabled ? (
    children
  ) : (
    <Tooltip
      title={
        <Typography color="textPrimary">
          {t("adjustmentsDisabledTooltip")}
        </Typography>
      }
    >
      {children}
    </Tooltip>
  );
};

const ValidateOrderAdjustments = ({
  order,
  surveyComments,
  scoreAdjusted,
  adjustedEmployee,
  onScoreChange,
  onCommentsChange,
  onEmployeeChange,
  isOrderCompleted,
  isReadyForReview,
  isUpdating
}) => {
  const theme = useTheme();
  const t = useScopedTranslation("validate.order.details");
  const classes = useStyles();
  const isReadyForReviewOrCompleted = isOrderCompleted || isReadyForReview;
  const employeeAdjustedBy =
    order.employeeIdAdjustedBy && order.employeeIdAdjustedBy.fullName;

  const scoreAdjustedBy =
    order.scoreAdjustedBy && order.scoreAdjustedBy.fullName;

  const surveyCommentsAdjustedBy = get(
    order,
    "surveyCommentsAdjustedBy.fullName"
  );

  const handleScoreAdjustedChanged = event => {
    if (!isNaN(event.target.value)) {
      const scoreInt = parseInt(event.target.value);
      onScoreChange(
        isNaN(scoreInt) ? "" : Math.max(Math.min(scoreInt, maxScore), 0)
      );
    }
  };

  const handleCommentsChanged = event => {
    onCommentsChange(event.target.value.substr(0, characterLimit));
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      bgcolor={theme.palette.secondary[50]}
      py={{ xs: 2, sm: 4 }}
      px={{ xs: 2, sm: 5 }}
      mt={2}
    >
      <Box display="flex" flexDirection="column" flex={1}>
        <Typography variant="h6" sx={{ pb: 2 }}>
          {t("adjustmentsHeader")}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} className={classes.adjustmentGridItem}>
            <AdjustmentField disabled={!isReadyForReviewOrCompleted}>
              <div>
                <UserSelect
                  disabled={!isReadyForReviewOrCompleted || isUpdating}
                  value={adjustedEmployee}
                  onChange={onEmployeeChange}
                  helperText={
                    order.employeeIdAdjustedAt
                      ? t("employeeAdjustedAt.label", {
                          date: formatDate(order.employeeIdAdjustedAt),
                          name: employeeAdjustedBy
                        })
                      : ""
                  }
                  error={
                    isOrderCompleted &&
                    !adjustedEmployee &&
                    !order.surveyEmployee
                  }
                />
              </div>
            </AdjustmentField>
          </Grid>
          <Grid item xs={12} sm={6} className={classes.adjustmentGridItem}>
            <AdjustmentField disabled={!isReadyForReviewOrCompleted}>
              <div>
                <TextField
                  variant="filled"
                  fullWidth
                  label={t("adjustedScore.label")}
                  value={scoreAdjusted != null ? scoreAdjusted : ""}
                  onChange={handleScoreAdjustedChanged}
                  helperText={
                    order.scoreAdjustedAt
                      ? t("scoreAdjustedAt.label", {
                          date: formatDate(order.scoreAdjustedAt),
                          name: scoreAdjustedBy
                        })
                      : ""
                  }
                  InputProps={{
                    disableUnderline: true,
                    className: classes.textField
                  }}
                  disabled={isUpdating || !isReadyForReviewOrCompleted}
                />
              </div>
            </AdjustmentField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("comments.label")}
              value={surveyComments}
              onChange={handleCommentsChanged}
              multiline
              rows={6}
              maxRows={Infinity}
              variant="filled"
              helperText={
                order.surveyCommentsAdjustedAt
                  ? t("commentsAdjustedAt.label", {
                      date: formatDate(order.surveyCommentsAdjustedAt),
                      name: surveyCommentsAdjustedBy || "Unknown"
                    })
                  : ""
              }
              InputProps={{
                disableUnderline: true,
                className: classes.textField
              }}
              disabled={isUpdating}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

ValidateOrderAdjustments.propTypes = {
  order: PropTypes.object,
  comments: PropTypes.string,
  scoreAdjusted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  adjustedEmployee: PropTypes.object,
  onScoreChange: PropTypes.func,
  onCommentsChange: PropTypes.func,
  onEmployeeChange: PropTypes.func,
  isOrderCompleted: PropTypes.bool
};

export default ValidateOrderAdjustments;
